/* ============================================================
   MOSAIK — STATS  (usage analytics + PDF archiving)

   Single instrumentation point for the whole configurator. Every other
   module calls MosaikStats and knows nothing about the backend.

   Events emitted:
     page_view        — configurator loaded
     config_start     — visitor validated a collection + dimensions (1st time)
     collection_change— visitor switched collection afterwards
     pdf_generated    — PDF exported (the PDF file itself is uploaded too)
     add_to_cart      — design sent to the Shopify cart
     template_save_click     — "Enregistrer" clicked  (mosaik-mytemplates.js)
     template_login_redirect — logged-out visitor accepts to go and sign in

   The counters that follow (template_saved / template_deleted /
   account_via_save) are written by the Worker itself once the App Proxy
   signature checks out — a browser cannot post them.

   Two sinks, both optional and both fail-silent:
     1. window.MOSAIK_STATS_ENDPOINT — Cloudflare Worker (see backend/).
        Stores one row per event in D1 + a copy of each PDF in R2.
     2. gtag() — GA4, if the theme already loaded it. Adds traffic-source
        attribution, which the Worker cannot know. Nested objects are
        flattened away since GA4 only accepts scalar event params.

   HARD RULE: analytics must never break the configurator. Every public
   method is wrapped in try/catch and every network call is fire-and-forget.
   ============================================================ */
window.MosaikStats = {

  endpoint: (window.MOSAIK_STATS_ENDPOINT || '').replace(/\/+$/, ''),

  _booted: false,
  _configStarted: false,

  /* ---------- identity ----------
     vid = visitor, persists across visits (localStorage)
     sid = session, one per tab/visit (sessionStorage)
     Both are random opaque ids: no personal data, nothing to correlate
     with a customer account. */
  _uuid:function(){
    if(window.crypto && crypto.randomUUID) return crypto.randomUUID();
    // Safari < 15.4 and old Android browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  },
  _id:function(store, key){
    try {
      let v = store.getItem(key);
      if(!v){ v = this._uuid(); store.setItem(key, v); }
      return v;
    } catch(e){ return 'nostorage'; }   // private mode / storage blocked
  },
  vid:function(){ return this._id(window.localStorage,   'mosaik_vid'); },
  sid:function(){ return this._id(window.sessionStorage, 'mosaik_sid'); },

  /* ---------- state snapshot ----------
     Reads the live configurator state. Everything here comes from data the
     recap already computed (mosaik._lastRecap / mosaik._total), so the
     numbers shown to the visitor and the numbers we record cannot diverge. */
  snapshot:function(){
    const m    = window.mosaik || {};
    const o    = m.options || {};
    const lay  = m.layout   || {};
    const r    = m._lastRecap || {};
    const lang = o.lang || 'fr';

    // Recap tables are keyed by localized name. tiles holds the pile object
    // itself (with .count), borders/corners wrap it as {count, obj} — either
    // way .count is at the top level, so one reducer covers all three.
    const flat = tbl => {
      const out = {};
      Object.keys(tbl || {}).forEach(k => { out[k] = (tbl[k] || {}).count || 0; });
      return out;
    };
    const tiles   = flat(r.tiles);
    const borders = flat(r.borders);
    const corners = flat(r.corners);
    const sum     = obj => Object.values(obj).reduce((a, b) => a + b, 0);

    let lights = 0;
    if(window.MosaikLights && MosaikLights.enabled)
      lights = Object.keys(m.lightAssignments || {}).length;

    return {
      lang:        lang,
      device:      (window.innerWidth < 768 ? 'mobile' : 'desktop'),
      collection:  o.currentCollection || null,
      width:       o.width  || 0,
      height:      o.height || 0,
      cols:        lay.cols || 0,
      rows:        lay.rows || 0,
      fill:        o.fill || null,
      tiles:       tiles,
      borders:     borders,
      corners:     corners,
      tilesTotal:  sum(tiles),
      bordersTotal:sum(borders),
      cornersTotal:sum(corners),
      lights:      lights,
      totalPrice:  +(m._total || 0).toFixed(2),
      url:         location.href,          // carries ?collection=&w=&h= → design is re-openable
      referrer:    document.referrer || ''
    };
  },

  /* ---------- transport ---------- */

  // text/plain keeps the request CORS-"simple": no preflight, so sendBeacon
  // still fires reliably when the visitor is leaving the page. The Worker
  // parses the body as JSON regardless of the declared content type.
  _post:function(path, body){
    if(!this.endpoint) return;
    const url  = this.endpoint + path;
    const json = JSON.stringify(body);
    try {
      if(navigator.sendBeacon){
        const blob = new Blob([json], { type: 'text/plain;charset=UTF-8' });
        if(navigator.sendBeacon(url, blob)) return;
      }
    } catch(e){ /* fall through to fetch */ }
    try {
      fetch(url, {
        method:      'POST',
        headers:     { 'Content-Type': 'text/plain;charset=UTF-8' },
        body:        json,
        keepalive:   true,
        credentials: 'omit',
        mode:        'cors'
      }).catch(() => {});
    } catch(e){ /* offline: drop the event */ }
  },

  // GA4 only accepts scalar params, so the per-colour breakdowns are dropped
  // here and kept only in D1.
  _ga:function(event, s){
    try {
      if(typeof window.gtag !== 'function') return;
      window.gtag('event', event, {
        mosaik_collection: s.collection || '(none)',
        mosaik_area:       +((s.width || 0) * (s.height || 0)).toFixed(2),
        mosaik_tiles:      s.tilesTotal || 0,
        mosaik_device:     s.device,
        value:             s.totalPrice || 0,
        currency:          'EUR'
      });
    } catch(e){ /* gtag misconfigured: ignore */ }
  },

  /* ---------- public API ---------- */

  track:function(event, extra){
    try {
      const s = this.snapshot();
      if(extra) Object.assign(s, extra);
      this._post('/e', {
        id: this._uuid(), event: event, vid: this.vid(), sid: this.sid(), data: s
      });
      this._ga(event, s);
    } catch(e){ /* never break the caller */ }
  },

  // Called by mosaik-pdf.js once the download has been handed to the browser.
  // The metadata row and the PDF blob travel together so the backend can link
  // them without a second round-trip.
  pdfGenerated:function(blob){
    try {
      const s  = this.snapshot();
      const id = this._uuid();
      this._ga('pdf_generated', s);
      if(!this.endpoint) return;
      // sendBeacon is capped at 64 KB by most browsers — a PDF never fits,
      // so this one goes through fetch. multipart/form-data is CORS-simple.
      const fd = new FormData();
      fd.append('meta', JSON.stringify({ id: id, event: 'pdf_generated', vid: this.vid(), sid: this.sid(), data: s }));
      if(blob) fd.append('pdf', blob, 'mosaik.pdf');
      fetch(this.endpoint + '/pdf', {
        method: 'POST', body: fd, credentials: 'omit', mode: 'cors'
      }).catch(() => {
        // Upload failed (offline, blocked, too large): still record the event
        // so the counters stay right even when the archive copy is lost.
        this._post('/e', { id: id, event: 'pdf_generated', vid: this.vid(), sid: this.sid(), data: s });
      });
    } catch(e){ /* export must succeed even if archiving does not */ }
  },

  // Called by mosaik-collection.js whenever a setup is applied (popup
  // validation or ?collection= URL prefill). First one is the funnel entry.
  setupApplied:function(){
    try {
      if(this._configStarted){ this.track('collection_change'); }
      else { this._configStarted = true; this.track('config_start'); }
    } catch(e){}
  },

  boot:function(){
    try {
      if(this._booted) return;
      this._booted = true;
      this.track('page_view');
    } catch(e){}
  }
};

// Self-boot: page_view is the funnel denominator (visitors who landed but
// never picked a collection), so it must fire without any other module.
document.addEventListener('DOMContentLoaded', function(){
  // Deferred one tick so mosaik.init() has run and options.lang is set.
  setTimeout(() => MosaikStats.boot(), 0);
});
